import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, NavLink, useParams } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon, People as PeopleIcon } from '@mui/icons-material';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useAuth } from '@contexts/AuthContext';
import API from '@utils/axios';
import { formError } from '@utils/helpers';
import Layout from '@components/Layout';
import CustomerTabs from '@components/customer/CustomerTabs';
import CustomerForm from '@components/customer/CustomerForm';
import FormSkeleton from '@components/FormSkeleton';

function CustomerEditPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { showMessage } = useSnackbar();
    const { refreshAuth } = useAuth();
    const [initialData, setInitialData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const form = useRef();

    const fetchData = useCallback(async () => {
        try {
            const response = await API.get(`/customers/${id}`);
            
            if (response.data.logo) {
                setInitialData({
                    ...response.data,
                    logoUrl: response.data.logo
                });
            } else {
                setInitialData(response.data);
            }
        } catch (error) {
            showMessage(t('Failed to load data'), 'error');
        } finally {
            setLoaded(true);
        }
    }, [id, t, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (data) => {
        try {
            await API.put(`/customers/${id}`, data);
            showMessage(t('Customer updated successfully'), 'success');
            refreshAuth();
        } catch (error) {
            if (form.current) {
                formError(error, t, form.current.setError, showMessage);
            } else {
                showMessage(t('An error occurred'), 'error');
            }
        }
    };

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/customers" underline="hover">{t('Customer management')}</Link>
                    <Typography>{initialData?.name}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to="/customers" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            <CustomerTabs customer={initialData} currentTab="infos" />
            {loaded ?
                <CustomerForm initialData={initialData} onSubmit={handleSubmit} ref={form}
                    buttons={
                        <Link to="/users" component={NavLink} underline="none" color="inherit"
                            onClick={() => {
                                localStorage.setItem('users.searchVisible', true);
                                localStorage.setItem('users.searchValues', JSON.stringify({ customer_id: id }));
                            }}>
                            <Button variant="outlined" sx={{ ml: 2 }} startIcon={<PeopleIcon />}>
                                {t('View users')}
                            </Button>
                        </Link>
                    } />
                : <FormSkeleton nb={4} />
            }
        </Layout>
    );
}

export default CustomerEditPage;
